import { AxiosResponse } from 'axios';
import { BackorderReportSearchResponse, BackorderReportSearchCriteria } from './types';
import { createApiClient } from 'services/apiClient';
import { performBulkDelete, BulkDeleteResult, BulkDeleteOptions } from 'services/bulkDelete';
import { API_BASE_URL } from 'config/api';

const apiClient = createApiClient('BACKORDER_REPORT');

export default class BackorderReportService {
  static async searchBackorderReports(tenantId: string, criteria: BackorderReportSearchCriteria): Promise<BackorderReportSearchResponse> {
    try {
      console.log('📦 [BACKORDER_REPORT] Searching backorder reports:', {
        tenantId,
        criteria,
        url: `${API_BASE_URL}/business-documents/backorder-reports/search/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      
      // Add query parameters
      if (criteria.document_status) params.append('document_status', criteria.document_status);
      if (criteria.created_at_start) params.append('created_at_start', criteria.created_at_start);
      if (criteria.created_at_end) params.append('created_at_end', criteria.created_at_end);
      if (criteria.document_state_id) params.append('document_state_id', criteria.document_state_id);
      if (criteria.has_been_sent != null) params.append('has_been_sent', criteria.has_been_sent.toString());
      if (criteria.parent_process_state_id) params.append('parent_process_state_id', criteria.parent_process_state_id);
      if (criteria.supplier_name) params.append('supplier_name', criteria.supplier_name);
      if (criteria.max_retry_reached != null) params.append('max_retry_reached', criteria.max_retry_reached.toString());
      if (criteria.has_errors != null) params.append('has_errors', criteria.has_errors.toString());
      if (criteria.has_work_description != null) params.append('has_work_description', criteria.has_work_description.toString());
      if (criteria.days_back) params.append('days_back', criteria.days_back.toString());
      if (criteria.limit) params.append('limit', criteria.limit.toString());
      
      // Add include flags
      params.append('include_parsed_document', 'true');
      params.append('include_processing_result', 'true');
      
      const url = `/business-documents/backorder-reports/search/${tenantId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<BackorderReportSearchResponse> = await apiClient.get(url);
      
      console.log('✅ [BACKORDER_REPORT] Backorder report search response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          total_results: response.data.summary.total_results,
          result_count: response.data.results?.length || 0
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [BACKORDER_REPORT] Error searching backorder reports:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to search backorder reports');
    }
  }

  static async getBackorderReportStateDetail(tenantId: string, reportStateId: string): Promise<any> {
    try {
      console.log('📦 [BACKORDER_REPORT] Fetching backorder report state detail:', {
        tenantId,
        reportStateId,
        url: `${API_BASE_URL}/business-documents/backorder-reports/state/${tenantId}/${reportStateId}`,
        method: 'GET'
      });
      
      const response: AxiosResponse<any> = await apiClient.get(`/business-documents/backorder-reports/state/${tenantId}/${reportStateId}`);
      
      console.log('✅ [BACKORDER_REPORT] Backorder report state detail response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: tenantId,
          document_state_id: response.data.document_state_id,
          document_status: response.data.document_status,
          supplier_name: response.data.parsed_document?.supplier_name
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [BACKORDER_REPORT] Error fetching backorder report state detail:', {
        tenantId,
        reportStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch backorder report state detail');
    }
  }

  static async updateBackorderReport(tenantId: string, reportStateId: string, backorderReportData: any): Promise<any> {
    try {
      console.log('📝 [BACKORDER_REPORT] Updating backorder report:', {
        tenantId,
        reportStateId,
        url: `${API_BASE_URL}/business-documents/backorder-reports/state/${tenantId}/${reportStateId}`,
        method: 'PUT'
      });
      
      const url = `/business-documents/backorder-reports/state/${tenantId}/${reportStateId}`;
      
      const response: AxiosResponse<any> = await apiClient.put(url, backorderReportData);
      
      console.log('✅ [BACKORDER_REPORT] Update backorder report response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [BACKORDER_REPORT] Error updating backorder report:', {
        tenantId,
        reportStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to update backorder report');
    }
  }

  static async deleteBackorderReportState(
    tenantId: string,
    reportStateId: string,
    force: boolean = false
  ): Promise<void> {
    try {
      console.log('🗑️ [BACKORDER_REPORT] Deleting backorder report state:', {
        tenantId,
        reportStateId,
        force,
        url: `${API_BASE_URL}/business-documents/backorder-reports/state/${tenantId}/${reportStateId}`,
        method: 'DELETE'
      });
      
      const params = new URLSearchParams();
      if (force) {
        params.append('force', 'true');
      }
      
      const url = `/business-documents/backorder-reports/state/${tenantId}/${reportStateId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<any> = await apiClient.delete(url);
      
      console.log('✅ [BACKORDER_REPORT] Delete backorder report state response:', {
        status: response.status,
        statusText: response.statusText
      });
    } catch (error: any) {
      console.error('❌ [BACKORDER_REPORT] Error deleting backorder report state:', {
        tenantId,
        reportStateId,
        force,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      
      // Re-throw with more detailed error information
      if (error.response?.data?.detail) {
        const errorMessage = new Error(error.response.data.detail);
        (errorMessage as any).status = error.response?.status;
        throw errorMessage;
      }
      throw error;
    }
  }

  static async bulkDeleteBackorderReportStates(
    tenantId: string,
    ids: string[],
    options?: BulkDeleteOptions,
  ): Promise<BulkDeleteResult> {
    return performBulkDelete(
      apiClient,
      'BACKORDER_REPORT',
      `/business-documents/backorder-reports/state/${tenantId}/bulk-delete`,
      ids,
      options,
    );
  }
}

